import { Link } from "wouter";
import { AlertTriangle, Check } from "lucide-react";
import { HAZARD_LABELS } from "@/lib/results";
import type { ReadinessStats } from "@/lib/dashboard-stats";

interface ReadinessPanelProps {
  stats: ReadinessStats;
  coverageLoading: boolean;
  coverageError: string | null;
}

interface ReadinessRow {
  label: string;
  /** 0 means this row is satisfied. Drives the marker — nothing else does. */
  actionableCount: number;
  value: string;
  /** Extra context shown under the value; never affects the marker. */
  note?: string;
  href: string;
}

function RowMarker({ satisfied }: { satisfied: boolean }) {
  // role="img" alongside aria-label — a bare <svg> with only aria-label is
  // announced unreliably by screen readers.
  return satisfied ? (
    <Check role="img" aria-label="Complete" className="h-4 w-4 shrink-0 text-emerald-600" />
  ) : (
    <AlertTriangle role="img" aria-label="Outstanding" className="h-4 w-4 shrink-0 text-amber-600" />
  );
}

function Row({ row }: { row: ReadinessRow }) {
  return (
    <Link
      href={row.href}
      className="flex items-start justify-between gap-4 rounded-lg px-2 py-3 transition-colors hover:bg-muted"
    >
      <div className="flex items-start gap-2">
        <RowMarker satisfied={row.actionableCount === 0} />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">{row.label}</span>
          {row.note && <span className="text-xs leading-5 text-muted-foreground">{row.note}</span>}
        </div>
      </div>
      <span className="shrink-0 text-sm font-semibold">{row.value}</span>
    </Link>
  );
}

export function ReadinessPanel({ stats, coverageLoading, coverageError }: ReadinessPanelProps) {
  const rows: ReadinessRow[] = [
    {
      label: "Assets missing coordinates",
      actionableCount: stats.assetsMissingCoordinates,
      value: String(stats.assetsMissingCoordinates),
      note:
        stats.assetsMissingCoordinates > 0
          ? "Without coordinates these assets get no hazard scores."
          : undefined,
      href: "/assessment#site-data",
    },
    {
      label: "Assets with no value chain stage",
      actionableCount: stats.assetsMissingStage,
      value: String(stats.assetsMissingStage),
      href: "/assessment#site-data",
    },
    {
      label: "Value chain importance not yet set",
      actionableCount: stats.stagesUnrated,
      value: `${stats.stagesUnrated} of ${stats.stagesTotal}`,
      href: "/assessment#value-chain-data",
    },
  ];

  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <h2 className="text-lg font-bold leading-7">Assessment readiness</h2>
      <div className="mt-2 flex flex-col divide-y divide-border">
        {rows.map((row) => (
          <Row key={row.label} row={row} />
        ))}
        <CoverageRow stats={stats} loading={coverageLoading} error={coverageError} />
      </div>
    </section>
  );
}

function CoverageRow({
  stats,
  loading,
  error,
}: {
  stats: ReadinessStats;
  loading: boolean;
  error: string | null;
}) {
  if (error) {
    return (
      <div className="flex items-start gap-2 px-2 py-3">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
        <span className="text-sm text-destructive">Couldn&apos;t load result coverage: {error}</span>
      </div>
    );
  }

  if (loading || stats.coverage === null) {
    return (
      <div className="flex items-center justify-between gap-4 px-2 py-3">
        <div className="h-4 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-10 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const { blockedByMissingSiteData, awaitingPlatformData, awaitingPlatformDataHazards, notScoredDrySite } =
    stats.coverage;
  const hazardNames = awaitingPlatformDataHazards.map((h) => HAZARD_LABELS[h]).join(", ");

  // The marker keys off the fixable bucket ONLY: awaitingPlatformData is
  // outside the workspace's control, so it must never turn this row amber.
  // The label carries what the number means — unlike the other three rows,
  // "Result coverage — 4" is not self-describing. Spec §2 words these two
  // figures as "N rows blocked by missing site data" / "N rows awaiting
  // platform data", and the note is always present so a 0 still explains
  // itself.
  const platformNote =
    awaitingPlatformData > 0
      ? `${awaitingPlatformData} rows awaiting platform data (${hazardNames}) — not something you can resolve.`
      : "Nothing outstanding on the platform side.";
  // Dry-site precipitation rows are a deliberate "not scored", not a gap.
  const drySiteNote =
    notScoredDrySite > 0
      ? ` ${notScoredDrySite} precipitation rows not scored: seasonal rainfall <50 mm (by design).`
      : "";

  return (
    <Row
      row={{
        label: "Result rows blocked by missing site data",
        actionableCount: blockedByMissingSiteData,
        value: String(blockedByMissingSiteData),
        note: platformNote + drySiteNote,
        href: "/assessment/detailed",
      }}
    />
  );
}
